/**
 * Formatadores de data da aba "Meus Estudos" (Flux Personalizado, #1991).
 *
 * - `DateOnly` ('YYYY-MM-DD': horizonte, blocos) é dia civil, sem hora nem fuso: formata por
 *   manipulação de string, nunca com `Date`. `new Date('2026-10-05')` é meia-noite UTC e
 *   apareceria como 04/10 em São Paulo.
 * - `Instant` (ISO com 'Z': execução, resposta, envio) é um momento: formata com `Intl` em
 *   America/Sao_Paulo, 24 h, igual para quem abre a tela de qualquer fuso. A base de fusos do
 *   navegador cuida do horário de verão histórico (antes de 2019 São Paulo chegou a ser UTC-2).
 */

import { DateOnly, Instant } from 'types/Flux/Personalized/myStudies';

const SAO_PAULO = 'America/Sao_Paulo';

/** Exibido no lugar de uma data que não dá para ler (o adapter já filtra; é só defesa). */
const UNREADABLE = '—';

/** Maior distância da época que um `Date` representa; além disso o `Intl` lança RangeError. */
const MAX_EPOCH_MS = 8.64e15;

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const SAO_PAULO_PARTS = new Intl.DateTimeFormat('pt-BR', {
  timeZone: SAO_PAULO,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

interface SaoPauloParts {
  /** 'YYYY-MM-DD' do dia civil em São Paulo. */
  dayKey: string;
  /** 'dd/mm/aaaa'. */
  date: string;
  /** 'HH:MM', 24 h. */
  time: string;
}

function toSaoPauloParts(epochMs: number): SaoPauloParts | null {
  if (!Number.isFinite(epochMs) || Math.abs(epochMs) > MAX_EPOCH_MS) return null;
  const parts = SAO_PAULO_PARTS.formatToParts(epochMs);
  const part = (type: Intl.DateTimeFormatPartTypes) => (
    parts.find((item) => item.type === type)?.value ?? ''
  );
  const year = part('year');
  const month = part('month');
  const day = part('day');

  return {
    dayKey: `${year}-${month}-${day}`,
    date: `${day}/${month}/${year}`,
    time: `${part('hour')}:${part('minute')}`,
  };
}

/** Dia civil anterior a 'YYYY-MM-DD', por aritmética de calendário em UTC (sem fuso da máquina). */
function previousDayKey(dayKey: string): string {
  const [year, month, day] = dayKey.split('-').map(Number);
  const previous = new Date(Date.UTC(year, month - 1, day - 1));
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${previous.getUTCFullYear()}-${pad(previous.getUTCMonth() + 1)}-${pad(previous.getUTCDate())}`;
}

/**
 * Milissegundos desde a época de um instante. A fração além dos milissegundos é truncada antes
 * do parse: com 7 ou mais dígitos o Safari arredonda e os outros motores truncam.
 *
 * @returns O instante em ms, ou NaN se o valor não for legível.
 */
export function instantToEpochMs(instant: Instant): number {
  return Date.parse(instant.replace(/(\.\d{3})\d+Z$/, '$1Z'));
}

/**
 * Data civil 'YYYY-MM-DD' como 'dd/mm/aaaa', só com manipulação de string.
 *
 * @param day - Dia civil no formato da D7.
 * @returns A data em pt-BR, ex.: '05/10/2026', ou '—' fora do formato.
 */
export function formatDateOnly(day: DateOnly): string {
  const match = DATE_ONLY_PATTERN.exec(day);
  if (!match) return UNREADABLE;
  const [, year, month, date] = match;
  return `${date}/${month}/${year}`;
}

/**
 * Instante completo em São Paulo, para o `title` da linha.
 *
 * @param instant - Instante ISO 8601 com 'Z'.
 * @returns 'dd/mm/aaaa HH:MM' (24 h), ou '—' se o valor não for legível.
 */
export function formatInstantFull(instant: Instant): string {
  const parts = toSaoPauloParts(instantToEpochMs(instant));
  return parts ? `${parts.date} ${parts.time}` : UNREADABLE;
}

/**
 * Dia civil de um instante em São Paulo, sem a hora: o "em 05/10/2026" do `criado_em` na linha
 * (#1998). Logo depois da meia-noite UTC ainda é a véspera em SP (08/10 02:30Z é 07/10).
 *
 * @param instant - Instante ISO 8601 com 'Z'.
 * @returns 'dd/mm/aaaa', ou '—' se o valor não for legível.
 */
export function formatInstantDate(instant: Instant): string {
  const parts = toSaoPauloParts(instantToEpochMs(instant));
  return parts ? parts.date : UNREADABLE;
}

/**
 * Instante relativo ao dia de hoje em São Paulo (.dc.html:574).
 *
 * @param instant - Instante ISO 8601 com 'Z'.
 * @param nowMs - Agora, em milissegundos desde a época (`Date.now()`), lido a cada render: um
 *   valor guardado faz "hoje" envelhecer depois da meia-noite.
 * @returns 'hoje, 14:20', 'ontem, 16:10' ou '05/10/2026, 18:00' (24 h); '—' se ilegível.
 */
export function formatInstantRelative(instant: Instant, nowMs: number): string {
  const parts = toSaoPauloParts(instantToEpochMs(instant));
  const today = toSaoPauloParts(nowMs);
  if (!parts || !today) return UNREADABLE;

  if (parts.dayKey === today.dayKey) return `hoje, ${parts.time}`;
  if (parts.dayKey === previousDayKey(today.dayKey)) return `ontem, ${parts.time}`;
  return `${parts.date}, ${parts.time}`;
}
